import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  aprobarSolicitud,
  listarProductoresActivos,
  obtenerDetalleSolicitud,
  rechazarSolicitud,
} from '../../services/adminSolicitudes';
import AppShell from '../../components/layout/AppShell';
import Button from '../../components/ui/Button';
import ConfirmModal from '../../components/ui/ConfirmModal';
import StatusBadge from '../../components/ui/StatusBadge';

function formatFecha(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-CL');
  } catch {
    return iso;
  }
}

function SolicitudDetailPage() {
  const { id } = useParams();

  const [solicitud, setSolicitud] = useState(null);
  const [productores, setProductores] = useState([]);
  const [productoresIds, setProductoresIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [motivo, setMotivo] = useState('');
  const [showRechazarForm, setShowRechazarForm] = useState(false);
  const [showAprobarModal, setShowAprobarModal] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [actionError, setActionError] = useState('');

  const cargar = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    setProductoresIds([]);

    const detalleResult = await obtenerDetalleSolicitud(id);
    if (!detalleResult.ok) {
      setLoadError(detalleResult.message);
      setSolicitud(null);
      setLoading(false);
      return;
    }

    setSolicitud(detalleResult.solicitud);

    if (detalleResult.solicitud?.estado === 'PENDIENTE') {
      const productoresResult = await listarProductoresActivos();
      if (productoresResult.ok) {
        setProductores(productoresResult.productores);
      }
    }

    setLoading(false);
  }, [id]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const productoresSeleccionados = productores.filter((p) => productoresIds.includes(p.id));

  const toggleProductor = (idproductor) => {
    setProductoresIds((prev) =>
      prev.includes(idproductor) ? prev.filter((id) => id !== idproductor) : [...prev, idproductor]
    );
  };

  const handleAprobar = async () => {
    setProcesando(true);
    setActionError('');
    const result = await aprobarSolicitud(solicitud.id, productoresIds);
    setProcesando(false);
    setShowAprobarModal(false);

    if (!result.ok) {
      setActionError(result.message);
      return;
    }
    cargar();
  };

  const handleRechazar = async () => {
    setProcesando(true);
    setActionError('');
    const result = await rechazarSolicitud(solicitud.id, motivo);
    setProcesando(false);

    if (!result.ok) {
      setActionError(result.message);
      return;
    }
    setShowRechazarForm(false);
    cargar();
  };

  if (loading) {
    return (
      <AppShell title="Solicitud" backTo="/admin/solicitudes">
        <p className="text-center text-sm text-gray-500">Cargando...</p>
      </AppShell>
    );
  }

  if (loadError || !solicitud) {
    return (
      <AppShell title="Solicitud" backTo="/admin/solicitudes">
        <p className="text-center text-sm text-red-600">{loadError || 'Solicitud no encontrada.'}</p>
      </AppShell>
    );
  }

  return (
    <AppShell title="Solicitud" backTo="/admin/solicitudes">
      <div className="flex flex-col gap-6">
        <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <h2 className="text-lg font-semibold text-gray-900">{solicitud.nombre}</h2>
            <StatusBadge estado={solicitud.estado} />
          </div>

          <dl className="mt-4 flex flex-col gap-4 text-sm">
            <div>
              <dt className="font-medium text-gray-500">Correo</dt>
              <dd className="mt-1 text-gray-900">{solicitud.email}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-500">Fecha solicitud</dt>
              <dd className="mt-1 text-gray-900">{formatFecha(solicitud.created_at)}</dd>
            </div>
            {solicitud.estado === 'RECHAZADA' && solicitud.motivo_rechazo && (
              <div>
                <dt className="font-medium text-gray-500">Motivo de rechazo</dt>
                <dd className="mt-1 text-gray-900">{solicitud.motivo_rechazo}</dd>
              </div>
            )}
          </dl>
        </div>

        {actionError && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{actionError}</p>}

        {solicitud.estado === 'PENDIENTE' && !showRechazarForm && (
          <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <p className="mb-1 block text-sm font-medium text-gray-700">Productores permitidos *</p>
              {productoresIds.length > 0 && (
                <p className="mb-1 text-xs text-gray-500">
                  {productoresIds.length} productor{productoresIds.length === 1 ? '' : 'es'} seleccionado
                  {productoresIds.length === 1 ? '' : 's'}
                </p>
              )}
            </div>

            {productores.length === 0 ? (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">
                No hay productores activos disponibles. Crea o activa al menos un productor antes de aprobar.
              </p>
            ) : (
              <div className="flex flex-col divide-y divide-gray-100 rounded-xl border border-gray-200">
                {productores.map((productor) => (
                  <label
                    key={productor.id}
                    className="flex min-h-[48px] items-center gap-3 px-4 text-sm text-gray-900"
                  >
                    <input
                      type="checkbox"
                      checked={productoresIds.includes(productor.id)}
                      onChange={() => toggleProductor(productor.id)}
                      className="h-5 w-5 shrink-0 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    {productor.nombre}
                  </label>
                ))}
              </div>
            )}

            <Button
              className="mt-4"
              disabled={productoresIds.length === 0}
              onClick={() => setShowAprobarModal(true)}
            >
              Aprobar solicitud
            </Button>

            <Button variant="secondary" className="mt-3" onClick={() => setShowRechazarForm(true)}>
              Rechazar solicitud
            </Button>
          </div>
        )}

        {solicitud.estado === 'PENDIENTE' && showRechazarForm && (
          <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
            <label htmlFor="motivo" className="mb-1 block text-sm font-medium text-gray-700">
              Motivo del rechazo
            </label>
            <textarea
              id="motivo"
              value={motivo}
              onChange={(event) => setMotivo(event.target.value)}
              rows={4}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 text-base text-gray-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-200"
              placeholder="Explica brevemente por qué se rechaza esta solicitud"
            />

            <Button variant="primary" className="mt-4" disabled={!motivo.trim() || procesando} onClick={handleRechazar}>
              {procesando ? 'Procesando...' : 'Confirmar rechazo'}
            </Button>
            <Button
              variant="secondary"
              className="mt-3"
              onClick={() => {
                setShowRechazarForm(false);
                setMotivo('');
              }}
              disabled={procesando}
            >
              Cancelar
            </Button>
          </div>
        )}
      </div>

      <ConfirmModal
        open={showAprobarModal}
        title="¿Aprobar este acceso?"
        confirmLabel="Aprobar"
        cancelLabel="Cancelar"
        confirming={procesando}
        onConfirm={handleAprobar}
        onCancel={() => setShowAprobarModal(false)}
      >
        <p>
          <span className="font-medium">Encargado:</span> {solicitud.nombre}
        </p>
        <p>
          <span className="font-medium">Productores:</span>{' '}
          {productoresSeleccionados.map((p) => p.nombre).join(', ') || '—'}
        </p>
      </ConfirmModal>
    </AppShell>
  );
}

export default SolicitudDetailPage;
